<template>
  <div class="chart-container" ref="chartRef"></div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, nextTick } from 'vue'
import * as echarts from 'echarts'
const  getUpPercent=(a, b)=> {
  // 基数为0
  if (b === 0) {
    // 原来是0，现在也是0 → 0%
    if (a === 0) return '0.00%'
    // 原来是0，现在有数字 → 暴涨 9999.99%
    return '+100%'
  }

  const percent = ((a - b) / b) * 100
  return percent.toFixed(2) + '%'
}
const stateText=(state,type,num)=>{
    let text=""
    if(state=='1'){
       text="比昨日"+type
    }
    if(state=='month'){
       text="比上个月"+type
    }
    if(state=='3month'){
       text="比3个月前"+type
    }
    if(state=='6month'){
       text="比6个月前"+type
    }
    if(state=='year'){
       text="比去年"+type
    }
    if(state=='6'){
       text=`比${num}天前`+type
    }
    return text
}
const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  oldData: {
    type: Object,
    default: () => ({}),
  },
  timeType: {
    type: String,
    default: "",
  },
  timeDay:{
    type: Number,
    default: 0,
  }
})

const chartRef = ref(null)
let chartInstance = null
let resizeObserver = null

// 扇区颜色（固定）
const sectorColors = ['#F3A129', '#337CFA']

// 响应式原始数据
const rawData = ref([
  { name: '未报警数量', value: 0 },
  { name: '报警数量', value: 0 },
])

// 根据 props.data 更新 rawData 的 value
const updateDataFromProps = () => {
  const { warningCount=0,nonWarningCount=0 } = props.data
  rawData.value = [
    { name: '未报警数量', value: nonWarningCount },
    { name: '实时报警数量', value: warningCount },
  ]
}

// 生成带 itemStyle 的图表数据
const getChartData = () => {
  return rawData.value.map((item, idx) => ({
    ...item,
    itemStyle: { color: sectorColors[idx] }
  }))
}

// 动态生成 rich 样式（基于固定颜色）
const generateRich = () => {
  const rich = {
    nameStyle: {
      color: '#ffffff',
      fontSize: 14
    }
  }
  sectorColors.forEach((color, idx) => {
    rich[`pct${idx}`] = {
      color: color,
      fontSize: 14
    }
  })
  return rich
}

// 构建图表配置（每次调用时获取最新数据）
const getChartOption = () => ({
  tooltip: {
    trigger: 'item',
    formatter: (params) => {
    return `<div style="text-align: center;">
              <div>${params.value}</div>
              <div>${params.name}</div>
            </div>`
    }
  },
  series: [{
    name: '销售占比',
    type: 'pie',
    radius: ['45%', '55%'],
    avoidLabelOverlap: false,
    data: getChartData(),
    label: {
      alignTo: 'edge',
        formatter: (params) => {
          const idx = params.dataIndex;
          let displayName=""
          let isPositive=null
          let res=null
          const { warningCount = 0, nonWarningCount = 0 } = props.data
          const { old_warningCount = 0, old_nonWarningCount = 0 } = props.oldData
          if(idx==0){
            isPositive = nonWarningCount >= 0;
             if(isPositive){
                  displayName = stateText(props.timeType,'增加',props.timeDay)
             }else{
                  displayName = stateText(props.timeType,'减少',props.timeDay)
             }
             res= getUpPercent(nonWarningCount,old_nonWarningCount)
          }
          if(idx==1){
             isPositive = warningCount >= 0;
             if(isPositive){
                  displayName = stateText(props.timeType,'增加',props.timeDay)
             }else{
                  displayName = stateText(props.timeType,'减少',props.timeDay)
             }
             res= getUpPercent(warningCount,old_warningCount)
          }
          return `{pct${idx}|${res}}\n{nameStyle|${displayName}}`;
        },
      minMargin: 5,
      edgeDistance: 40,
      lineHeight: 30,
      rich: generateRich(),
      label: {
      position: 'outside',      // 明确指定外部
      },
      labelLine: {
        showAbove: false,         // 标签线不覆盖扇区
        length: 15,
        length2: 120
      }
    },
    labelLine: {
      length: 15,
      length2: 120
    },
    labelLayout: (params) => {
      // if (!chartInstance) return
      // const isLeft = params.labelRect.x < chartInstance.getWidth() / 2
      // const points = params.labelLinePoints
      // points[2][0] = isLeft
      //   ? params.labelRect.x
      //   : params.labelRect.x + params.labelRect.width
      // return { labelLinePoints: points }
      return {}
    }
  }],
  graphic: {
    type: 'image',
    style: {
      image: 'https://echarts.apache.org/zh/images/favicon.png',
      width: 50,
      height: 50
    },
    left: 'center',
    top: 'center',
    z: 100
  }
})

// 刷新图表
const updateChart = () => {
  if (chartInstance) {
    chartInstance.setOption(getChartOption(), true) // true 表示完全替换配置
  }
}

// 初始化图表
const initChart = () => {
  if (chartRef.value) {
    chartInstance = echarts.init(chartRef.value)
    updateChart()
  }
}

// 监听 props.data 变化，更新数据并刷新图表
watch(() => props.data, () => {
  updateDataFromProps()
  nextTick(() => {
    updateChart()
  })
}, { deep: true, immediate: true }) // immediate 确保组件挂载前已初始化数据

// 窗口大小适配
const handleResize = () => {
  chartInstance?.resize()
}

const observeResize = () => {
  if (!chartRef.value) return
  resizeObserver = new ResizeObserver(() => {
    chartInstance?.resize()
  })
  resizeObserver.observe(chartRef.value)
}

onMounted(() => {
  updateDataFromProps()   // 初次数据同步
  initChart()
  window.addEventListener('resize', handleResize)
  observeResize()
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  resizeObserver?.disconnect()
  chartInstance?.dispose()
})
</script>

<style scoped>
.chart-container {
  flex: 1;
  width: 100%;
  min-height: 0;
}
</style>